import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Modal from '../Modal';
import {
  generateImages,
  estimateImageGenerationCost,
  getImageProviders,
  type ImageProvider,
  type ImageQuality,
  type ImageStyle,
  type Platform,
  type AspectRatio,
} from '../../lib/api';

interface GenerateImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  availablePlatforms: Platform[];
  onJobStarted?: (jobId: string) => void;
}

interface ProviderInfo {
  provider: ImageProvider;
  name: string;
  models: string[];
  supportedAspectRatios: AspectRatio[];
  supportsQuality: boolean;
  supportsStyle: boolean;
}

const defaultProviders: ProviderInfo[] = [
  {
    provider: 'openai_dalle3',
    name: 'OpenAI DALL-E 3',
    models: ['dall-e-3'],
    supportedAspectRatios: ['1:1', '16:9', '9:16'],
    supportsQuality: true,
    supportsStyle: true,
  },
  {
    provider: 'openai_dalle2',
    name: 'OpenAI DALL-E 2',
    models: ['dall-e-2'],
    supportedAspectRatios: ['1:1'],
    supportsQuality: false,
    supportsStyle: false,
  },
  {
    provider: 'gemini',
    name: 'Google Gemini Imagen',
    models: ['imagen-3.0-generate-001', 'imagen-3.0-fast-generate-001'],
    supportedAspectRatios: ['1:1', '16:9', '9:16', '4:5', '3:4', '2:3'],
    supportsQuality: false,
    supportsStyle: false,
  },
];

export default function GenerateImagesModal({
  isOpen,
  onClose,
  postId,
  availablePlatforms,
  onJobStarted,
}: GenerateImagesModalProps) {
  const { t } = useTranslation();

  // Provider state
  const [providers, setProviders] = useState<ProviderInfo[]>(defaultProviders);
  const [selectedProvider, setSelectedProvider] = useState<ImageProvider>('openai_dalle3');
  const [selectedModel, setSelectedModel] = useState<string>('dall-e-3');

  // Generation config
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(availablePlatforms);
  const [variantsPerPlatform, setVariantsPerPlatform] = useState(2);
  const [quality, setQuality] = useState<ImageQuality>('standard');
  const [style, setStyle] = useState<ImageStyle>('vivid');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);

  // Load providers on mount
  useEffect(() => {
    if (isOpen) {
      loadProviders();
    }
  }, [isOpen]);

  // Update estimated cost when config changes
  useEffect(() => {
    if (isOpen && selectedPlatforms.length > 0) {
      updateCostEstimate();
    }
  }, [selectedProvider, selectedModel, selectedPlatforms, variantsPerPlatform, quality, isOpen]);

  const loadProviders = async () => {
    try {
      const { providers: apiProviders } = await getImageProviders(true);
      // For now, use default providers (API might return detailed info in future)
      // setProviders(apiProviders);
    } catch (error) {
      console.error('Failed to load providers:', error);
      // Use default providers on error
    }
  };

  const updateCostEstimate = async () => {
    setIsEstimating(true);
    try {
      // Build request object, only include optional fields if they have values
      const request: any = {
        provider: selectedProvider,
        platforms: selectedPlatforms,
        variantsPerPlatform,
      };

      // Only add optional fields if they have values
      if (selectedModel) request.model = selectedModel;
      if (quality) request.quality = quality;

      const { estimatedCostTokens } = await estimateImageGenerationCost(request);
      setEstimatedCost(estimatedCostTokens);
    } catch (error) {
      console.error('Failed to estimate cost:', error);
      setEstimatedCost(null);
    } finally {
      setIsEstimating(false);
    }
  };

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const handleProviderChange = (provider: ImageProvider) => {
    setSelectedProvider(provider);
    const providerInfo = providers.find((p) => p.provider === provider);
    if (providerInfo && providerInfo.models.length > 0) {
      setSelectedModel(providerInfo.models[0]);
    }
  };

  const handleGenerate = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error(t('imageGeneration.errors.noPlatforms'));
      return;
    }

    setIsLoading(true);
    try {
      // Build request object, only include optional fields if they have values
      const request: any = {
        postId,
        provider: selectedProvider,
        platforms: selectedPlatforms,
        variantsPerPlatform,
      };

      // Only add optional fields if they have values
      if (selectedModel) request.model = selectedModel;
      if (quality) request.quality = quality;
      if (style) request.style = style;

      const { jobId } = await generateImages(request);

      toast.success(t('imageGeneration.success.generated'));
      onJobStarted?.(jobId);
      onClose();
    } catch (error: any) {
      console.error('Failed to generate images:', error);
      toast.error(error.response?.data?.error || t('imageGeneration.errors.generationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedProviderInfo = providers.find((p) => p.provider === selectedProvider);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('imageGeneration.title')} size="lg">
      <div className="space-y-6">
        {/* Subtitle */}
        <p className="text-gray-600 dark:text-gray-400">{t('imageGeneration.subtitle')}</p>

        {/* Provider Selection */}
        <div>
          <label className="block text-sm font-bold mb-2 dark:text-gray-100">
            {t('imageGeneration.provider')}
          </label>
          <select
            value={selectedProvider}
            onChange={(e) => handleProviderChange(e.target.value as ImageProvider)}
            className="w-full px-4 py-3 border-2 border-black dark:border-white bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
            disabled={isLoading}
          >
            {providers.map((provider) => (
              <option key={provider.provider} value={provider.provider}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>

        {/* Model Selection */}
        {selectedProviderInfo && selectedProviderInfo.models.length > 1 && (
          <div>
            <label className="block text-sm font-bold mb-2 dark:text-gray-100">
              {t('imageGeneration.model')}
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-3 border-2 border-black dark:border-white bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
              disabled={isLoading}
            >
              {selectedProviderInfo.models.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Platform Selection */}
        <div>
          <label className="block text-sm font-bold mb-2 dark:text-gray-100">
            {t('imageGeneration.selectPlatforms')}
          </label>
          <div className="grid grid-cols-3 gap-3">
            {availablePlatforms.map((platform) => (
              <button
                key={platform}
                onClick={() => togglePlatform(platform)}
                className={`px-4 py-3 border-2 font-medium transition-colors ${
                  selectedPlatforms.includes(platform)
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white'
                }`}
                disabled={isLoading}
              >
                {t(`platforms.${platform}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Variants Per Platform */}
        <div>
          <label className="block text-sm font-bold mb-2 dark:text-gray-100">
            {t('imageGeneration.variantsPerPlatform')}: {variantsPerPlatform}
          </label>
          <input
            type="range"
            min="1"
            max="4"
            value={variantsPerPlatform}
            onChange={(e) => setVariantsPerPlatform(Number(e.target.value))}
            className="w-full"
            disabled={isLoading}
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>2</span>
            <span>3</span>
            <span>4</span>
          </div>
        </div>

        {/* Quality (DALL-E 3 only) */}
        {selectedProviderInfo?.supportsQuality && (
          <div>
            <label className="block text-sm font-bold mb-2 dark:text-gray-100">
              {t('imageGeneration.quality')}
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setQuality('standard')}
                className={`flex-1 px-4 py-3 border-2 font-medium transition-colors ${
                  quality === 'standard'
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white'
                }`}
                disabled={isLoading}
              >
                {t('imageGeneration.standard')}
              </button>
              <button
                onClick={() => setQuality('hd')}
                className={`flex-1 px-4 py-3 border-2 font-medium transition-colors ${
                  quality === 'hd'
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white'
                }`}
                disabled={isLoading}
              >
                {t('imageGeneration.hd')}
              </button>
            </div>
          </div>
        )}

        {/* Style (DALL-E 3 only) */}
        {selectedProviderInfo?.supportsStyle && (
          <div>
            <label className="block text-sm font-bold mb-2 dark:text-gray-100">
              {t('imageGeneration.style')}
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setStyle('vivid')}
                className={`flex-1 px-4 py-3 border-2 font-medium transition-colors ${
                  style === 'vivid'
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white'
                }`}
                disabled={isLoading}
              >
                {t('imageGeneration.vivid')}
              </button>
              <button
                onClick={() => setStyle('natural')}
                className={`flex-1 px-4 py-3 border-2 font-medium transition-colors ${
                  style === 'natural'
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white'
                }`}
                disabled={isLoading}
              >
                {t('imageGeneration.natural')}
              </button>
            </div>
          </div>
        )}

        {/* Cost Estimate */}
        <div className="bg-gray-100 dark:bg-gray-900 border-2 border-black dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <span className="font-bold dark:text-gray-100">
              {t('imageGeneration.estimatedCost')}
            </span>
            <span className="text-lg font-bold dark:text-gray-100">
              {isEstimating ? (
                <span className="text-gray-500">{t('common.loading')}...</span>
              ) : estimatedCost !== null && estimatedCost !== undefined ? (
                `${estimatedCost.toLocaleString()} tokens`
              ) : (
                '-'
              )}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {selectedPlatforms.length} {t('imageGeneration.selectPlatforms').toLowerCase()} ×{' '}
            {variantsPerPlatform} {t('imageGeneration.variantsPerPlatform').toLowerCase()} ={' '}
            {selectedPlatforms.length * variantsPerPlatform} {t('common.total', { defaultValue: 'total' }).toLowerCase()}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-6 py-3 border-2 border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white font-medium transition-colors"
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleGenerate}
            className="px-6 py-3 border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || selectedPlatforms.length === 0}
          >
            {isLoading ? t('imageGeneration.generating') : t('imageGeneration.generate')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
